import { useMemo, use } from 'react';
import { useRootScope, useOfflineModeManager } from 'FrontRoyalAngular';
import { useLocation, useParams } from 'react-router-dom';
import { type AllowedOfflineFn } from './ProtectedRoute.types';

const trueCallback = () => Promise.resolve(true);
const falseCallback = () => Promise.resolve(false);

export function useEnterOrLeaveOfflineMode(allowedOffline?: boolean | AllowedOfflineFn) {
    const offlineModeManager = useOfflineModeManager();
    const location = useLocation();
    const params = useParams();
    const $rootScope = useRootScope();
    const checkIfOfflineModeAllowed = useMemo(() => {
        if (typeof allowedOffline === 'function') return () => allowedOffline({ params });
        return allowedOffline ? trueCallback : falseCallback;
    }, [allowedOffline, params]);

    if (offlineModeManager.hasNetworkConnection) {
        offlineModeManager.inOfflineMode = false;
        return null;
    }

    if (offlineModeManager.inOfflineMode) {
        return null;
    }

    // We want to avoid suspending whenever possible. The only case where we really need to make an asynchronous check is if the user has no
    // network connection and is not already in offline mode. In that case, we will suspend while we check if we can enter offline mode. This can
    // be a little janky, because there is a brief flash of suspense which can be noticeable especially when navigating between multiple tabs
    // on the same page, but our app doesn't really support having no connection when not being in offline mode. Pretty soon the user will see the
    // lost connection error, so they shouldn't be navigating around too much anyway.
    const { sendHome } = use(
        offlineModeManager.enterOrLeaveOfflineModeDuringRouting({
            pathname: location.pathname,
            allowedOfflineCallback: checkIfOfflineModeAllowed,
        }),
    );

    return sendHome ? $rootScope.homePath : null;
}
